/** Cold Water Network Editor — React Query hooks */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    MockChiller,
    MockPlant,
    MockPowerContract,
    MockPump,
    MockCoolingSystem,
} from './types';

// ── Query keys ──────────────────────────────────────────────────────────

const QUERY_KEYS = {
    plants: ['cold-water', 'plants'] as const,
    chillers: ['cold-water', 'chillers'] as const,
    pumps: ['cold-water', 'pumps'] as const,
    powerContracts: ['cold-water', 'power-contracts'] as const,
    coolingSystems: ['cold-water', 'cooling-systems'] as const,
};

// ── Queries ─────────────────────────────────────────────────────────────

export function usePlants() {
    return useQuery<MockPlant[]>({
        queryKey: QUERY_KEYS.plants,
        queryFn: async () => {
            const response = await fetch('/api/cold-water/plants');
            if (!response.ok) throw new Error('Failed to fetch plants');
            return response.json();
        },
    });
}

export function useChillers() {
    return useQuery<MockChiller[]>({
        queryKey: QUERY_KEYS.chillers,
        queryFn: async () => {
            const response = await fetch('/api/cold-water/chillers');
            if (!response.ok) throw new Error('Failed to fetch chillers');
            return response.json();
        },
    });
}

export function usePumps() {
    return useQuery<MockPump[]>({
        queryKey: QUERY_KEYS.pumps,
        queryFn: async () => {
            const response = await fetch('/api/cold-water/pumps');
            if (!response.ok) throw new Error('Failed to fetch pumps');
            return response.json();
        },
    });
}

export function usePowerContracts() {
    return useQuery<MockPowerContract[]>({
        queryKey: QUERY_KEYS.powerContracts,
        queryFn: async () => {
            const response = await fetch('/api/cold-water/power-contracts');
            if (!response.ok) throw new Error('Failed to fetch power contracts');
            return response.json();
        },
    });
}

export function useCoolingSystems() {
    return useQuery<MockCoolingSystem[]>({
        queryKey: QUERY_KEYS.coolingSystems,
        queryFn: async () => {
            const response = await fetch('/api/cold-water/cooling-systems');
            if (!response.ok) throw new Error('Failed to fetch cooling systems');
            return response.json();
        },
    });
}

// ── Mutations ───────────────────────────────────────────────────────────

export function useCreateChiller() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: Partial<MockChiller>) => {
            const response = await fetch('/api/cold-water/chillers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create chiller');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chillers });
        },
    });
}

export function useUpdateNode() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ nodeId, data }: { nodeId: string; data: Record<string, unknown> }) => {
            const response = await fetch(`/api/cold-water/nodes/${nodeId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update node');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chillers });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pumps });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.powerContracts });
        },
    });
}

export function useDeleteNode() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (nodeId: string) => {
            const response = await fetch(`/api/cold-water/nodes/${nodeId}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete node');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chillers });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pumps });
        },
    });
}
